import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type {
  CompanyLocationDTO,
  CompanyLocationPaymentMethod,
} from "@wmart/shared-types";

const PAYMENT_METHOD_LABEL: Record<CompanyLocationPaymentMethod, string> = {
  card: "Card",
  bank_transfer: "Bank transfer",
  credit_terms: "Credit terms",
};

interface Props {
  companyId: string;
  locations: CompanyLocationDTO[];
  onSuccess: () => void | Promise<void>;
}

/**
 * Sub-list of locations rendered inside CompanyEdit (edit mode only).
 *
 * Receives locations from the parent via props (the parent already loaded
 * them via the GET /v1/admin/companies/:id endpoint which returns
 * CompanyDetailDTO with its `locations` array). On delete the parent
 * refetches via `onSuccess()`.
 *
 * Delete is wmart_admin only on the API; if the signed-in admin is
 * wmart_sales-only the call will 403 and the surfaced toast is the
 * single source of truth (no client-side role gating — see T12 disclosure).
 */
export function CompanyLocationsList({
  companyId,
  locations,
  onSuccess,
}: Props) {
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();
  const [busy, setBusy] = useState(false);

  async function deleteLocation(locId: string) {
    if (!confirm("Delete this location? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.delete(
        `/v1/admin/companies/${companyId}/locations/${locId}`,
      );
      notify("success", "Location deleted");
      await onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" className="p-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-title-lg text-ink-900">Locations</h2>
        <Button
          size="sm"
          onClick={() => navigate(`/companies/${companyId}/locations/new`)}
        >
          Add location
        </Button>
      </div>

      <div className="mt-4">
        {locations.length === 0 ? (
          <p className="py-4 text-center text-body-sm text-ink-500">
            No locations yet.
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Code</TH>
                <TH>Name</TH>
                <TH className="text-right">Discount %</TH>
                <TH>Address</TH>
                <TH>Payment methods</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {locations.map((l) => (
                <TR key={l.id}>
                  <TD className="font-mono text-body-sm">{l.code}</TD>
                  <TD>{l.name}</TD>
                  <TD className="text-right">
                    {l.discount_percent > 0 ? `${l.discount_percent}%` : "—"}
                  </TD>
                  <TD className="text-body-sm text-ink-500">
                    {l.address.line1}, {l.address.city} {l.address.postcode}
                  </TD>
                  <TD className="text-body-sm text-ink-700">
                    {l.allowed_payment_methods.length > 0
                      ? l.allowed_payment_methods
                          .map((m) => PAYMENT_METHOD_LABEL[m] ?? m)
                          .join(", ")
                      : "—"}
                  </TD>
                  <TD className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/companies/${companyId}/locations/${l.id}`,
                          )
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => void deleteLocation(l.id)}
                        disabled={busy}
                        className="text-danger"
                      >
                        Delete
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </Card>
  );
}
